import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  FileText, 
  TrendingUp, 
  DollarSign, 
  Users, 
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock
} from "lucide-react";
import { format } from "date-fns";
import { useCurrency } from "@/hooks/use-currency";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { useLanguage } from "@/hooks/use-language";

const Reports = () => {
  const { convertAmount, formatAmount, currency } = useCurrency();
  const { language } = useLanguage();

  const t = (key: string) => {
    const translations: Record<string, Record<string, string>> = {
      en: {
        title: "Reports & Overview",
        subtitle: "Comprehensive view of your business performance",
        totalRevenue: "Total Revenue",
        totalClients: "Total Clients",
        activeProjects: "Active Projects",
        completedProjects: "Completed Projects",
        recentProjects: "Recent Projects",
        recentInvoices: "Recent Invoices",
        serviceRequests: "Service Requests",
        revenueBreakdown: "Revenue Breakdown",
        subscriptionRevenue: "Subscription Revenue",
        invoiceRevenue: "Invoice Revenue",
        pending: "Pending",
        inProgress: "In Progress",
        completed: "Completed",
        noProjects: "No projects yet",
        noInvoices: "No invoices yet",
      },
      fr: {
        title: "Rapports et Aperçu",
        subtitle: "Vue complète de la performance de votre entreprise",
        totalRevenue: "Revenu Total",
        totalClients: "Clients Totaux",
        activeProjects: "Projets Actifs",
        completedProjects: "Projets Terminés",
        recentProjects: "Projets Récents",
        recentInvoices: "Factures Récentes",
        serviceRequests: "Demandes de Service",
        revenueBreakdown: "Répartition des Revenus",
        subscriptionRevenue: "Revenu d'Abonnement",
        invoiceRevenue: "Revenu de Facturation",
        pending: "En Attente",
        inProgress: "En Cours",
        completed: "Terminé",
        noProjects: "Aucun projet",
        noInvoices: "Aucune facture",
      },
      ar: {
        title: "التقارير والنظرة العامة",
        subtitle: "عرض شامل لأداء عملك",
        totalRevenue: "إجمالي الإيرادات",
        totalClients: "إجمالي العملاء",
        activeProjects: "المشاريع النشطة",
        completedProjects: "المشاريع المكتملة",
        recentProjects: "المشاريع الأخيرة",
        recentInvoices: "الفواتير الأخيرة",
        serviceRequests: "طلبات الخدمة",
        revenueBreakdown: "تقسيم الإيرادات",
        subscriptionRevenue: "إيرادات الاشتراكات",
        invoiceRevenue: "إيرادات الفواتير",
        pending: "قيد الانتظار",
        inProgress: "قيد التنفيذ",
        completed: "مكتمل",
        noProjects: "لا توجد مشاريع بعد",
        noInvoices: "لا توجد فواتير بعد",
      },
      it: {
        title: "Report e Panoramica",
        subtitle: "Vista completa delle prestazioni aziendali",
        totalRevenue: "Ricavi Totali",
        totalClients: "Clienti Totali",
        activeProjects: "Progetti Attivi",
        completedProjects: "Progetti Completati",
        recentProjects: "Progetti Recenti",
        recentInvoices: "Fatture Recenti",
        serviceRequests: "Richieste di Servizio",
        revenueBreakdown: "Ripartizione Ricavi",
        subscriptionRevenue: "Ricavi da Abbonamento",
        invoiceRevenue: "Ricavi da Fatturazione",
        pending: "In Attesa",
        inProgress: "In Corso",
        completed: "Completato",
        noProjects: "Nessun progetto",
        noInvoices: "Nessuna fattura",
      },
    };
    return translations[language]?.[key] || translations.en[key];
  };

  // Fetch all data with real-time sync
  const { data: projects } = useQuery({
    queryKey: ["reports-projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: subscriptions } = useQuery({
    queryKey: ["reports-subscriptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: invoices } = useQuery({
    queryKey: ["reports-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: serviceRequests } = useQuery({
    queryKey: ["reports-service-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_requests")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Enable real-time
  useRealtimeSubscription("projects", "reports-projects");
  useRealtimeSubscription("subscriptions", "reports-subscriptions");
  useRealtimeSubscription("invoices", "reports-invoices");
  useRealtimeSubscription("service_requests", "reports-service-requests");

  // Calculate metrics
  const totalRevenue = subscriptions?.reduce((sum, sub) => {
    if (sub.payment_status === "completed") {
      return sum + convertAmount(Number(sub.price), (sub.currency || "USD") as any, currency);
    }
    return sum;
  }, 0) || 0;

  const invoiceRevenue = invoices?.reduce((sum, inv) => {
    if (inv.status === "paid") {
      return sum + convertAmount(Number(inv.total_amount), (inv.currency || "USD") as any, currency);
    }
    return sum;
  }, 0) || 0;

  // Include automated clients from service requests
  const totalClients = new Set([
    ...(projects?.map(p => p.customer_id).filter(Boolean) || []),
    ...(subscriptions?.map(s => s.client_email).filter(Boolean) || []),
    ...(serviceRequests?.map(r => r.client_id).filter(Boolean) || []),
  ]).size;

  const activeProjects = projects?.filter(p => 
    p.status === "in-progress" || p.status === "planning"
  ).length || 0;

  const completedProjects = projects?.filter(p => 
    p.status === "completed"
  ).length || 0;

  const pendingRequests = serviceRequests?.filter(r => 
    r.status === "pending"
  ).length || 0;

  const stats = [
    {
      title: t("totalRevenue"),
      value: formatAmount(totalRevenue + invoiceRevenue, currency),
      icon: DollarSign,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      title: t("totalClients"),
      value: totalClients.toString(),
      icon: Users,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      title: t("activeProjects"),
      value: activeProjects.toString(),
      icon: Briefcase,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      title: t("completedProjects"),
      value: completedProjects.toString(),
      icon: CheckCircle2,
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
    },
  ];

  return (
    <div className="animate-fade-in space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div>
        <h1 className="text-3xl font-bold">{t("title")}</h1>
        <p className="text-muted-foreground mt-2">
          {t("subtitle")}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title} className="p-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
                <p className="text-2xl font-bold">{stat.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Projects */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{t("recentProjects")}</h2>
          </div>
          <div className="space-y-3">
            {projects?.slice(0, 5).map((project) => (
              <div key={project.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex-1">
                  <p className="font-medium">{project.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(project.created_at), "MMM dd, yyyy")}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  project.status === "completed" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" :
                  project.status === "in-progress" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" :
                  "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                }`}>
                  {project.status}
                </span>
              </div>
            ))}
            {(!projects || projects.length === 0) && (
              <p className="text-center text-muted-foreground py-4">{t("noProjects")}</p>
            )}
          </div>
        </Card>

        {/* Recent Invoices */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{t("recentInvoices")}</h2>
          </div>
          <div className="space-y-3">
            {invoices?.slice(0, 5).map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex-1">
                  <p className="font-medium">{invoice.invoice_number}</p>
                  <p className="text-sm text-muted-foreground">
                    {invoice.client_name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">
                    {invoice.currency} {Number(invoice.total_amount).toFixed(2)}
                  </p>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    invoice.status === "paid" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" :
                    invoice.status === "sent" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" :
                    "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                  }`}>
                    {invoice.status}
                  </span>
                </div>
              </div>
            ))}
            {(!invoices || invoices.length === 0) && (
              <p className="text-center text-muted-foreground py-4">{t("noInvoices")}</p>
            )}
          </div>
        </Card>

        {/* Service Requests Status */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{t("serviceRequests")}</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-orange-100 dark:bg-orange-900/30">
              <span className="text-sm font-medium">{t("pending")}</span>
              <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">{pendingRequests}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <span className="text-sm font-medium">{t("inProgress")}</span>
              <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {serviceRequests?.filter(r => r.status === "in-progress").length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
              <span className="text-sm font-medium">{t("completed")}</span>
              <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                {serviceRequests?.filter(r => r.status === "completed").length || 0}
              </span>
            </div>
          </div>
        </Card>

        {/* Revenue Breakdown */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{t("revenueBreakdown")}</h2>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{t("subscriptionRevenue")}</span>
                <span className="text-sm font-bold">{formatAmount(totalRevenue, currency)}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ 
                    width: `${totalRevenue + invoiceRevenue > 0 ? (totalRevenue / (totalRevenue + invoiceRevenue)) * 100 : 0}%` 
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{t("invoiceRevenue")}</span>
                <span className="text-sm font-bold">{formatAmount(invoiceRevenue, currency)}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-secondary h-2 rounded-full transition-all"
                  style={{ 
                    width: `${totalRevenue + invoiceRevenue > 0 ? (invoiceRevenue / (totalRevenue + invoiceRevenue)) * 100 : 0}%` 
                  }}
                />
              </div>
            </div>
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{t("totalRevenue")}</span>
                <span className="text-2xl font-bold text-primary">
                  {formatAmount(totalRevenue + invoiceRevenue, currency)}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
